/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
/* eslint-disable arrow-body-style */
import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, Button, Tab, TextField } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useNavigate } from 'react-router-dom';

import MainCard from 'ui-component/cards/MainCard';
import TabPanel, { CTabs, a11yProps } from 'components/reusable/CTabPanel';
import InProgress from './InProgress';
import Signed from './Signed';
import Filters, { getFilterValues } from 'components/common/Filters';
import useAuth from 'hooks/useAuth';
import { useGetTherapistListQuery } from 'store/services/therapy';
import { useServiceListQuery } from 'store/services/core';
import { getEmployeeName } from 'utils/common';
import usePermission from 'hooks/usePermission';
import { useGetLocationCustomListQuery } from 'store/services/location';

const initialFilters = [
    {
        id: 'locations',
        label: 'Locations',
        type: 'checkbox',
        multiple: true,
        options: [],
        default: []
    },
    {
        id: 'service_id',
        label: 'Services',
        type: 'checkbox',
        multiple: true,
        options: [],
        default: []
    }
];

const ListActivityTimeSheet = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const [filters, setFilters] = useState(initialFilters);

    const [filterValues, setFilterValues] = useState(getFilterValues(initialFilters));
    const [therapistOpen, setTherapistOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const [therapist, setTherapist] = useState(0);
    const handleTabChange = useCallback((_, newIndex) => setTabIndex(newIndex), []);

    const { hasPermission, isTherapist: isRoleTherapist, isSameAsMasterAdmin } = usePermission();
    const isTherapist = isRoleTherapist && !isSameAsMasterAdmin;

    const locationPayload = {
        filters: {
            ...(user?.client_id && { client_id: user.client_id }),
            is_fee_for_service: 1,
            order_by: 'name',
            order: 'asc'
        }
    };
    // therapist dropdown data
    const { data: therapistData, isLoading: therapistIsLoading } = useGetTherapistListQuery(
        {
            filters: {
                status: 1,
                ...(user?.client_id && { clients: [user.client_id] })
            }
        },
        { skip: !user?.client_id }
    );
    const { data: locationList, isLoading: locationIsLoading } = useGetLocationCustomListQuery({
        payload: locationPayload,
        subType: 'LIST_DROPDOWN'
    });

    const { data: serviceList, isLoading: serviceIsLoading } = useServiceListQuery({
        filters: {
            status: 1,
            ...(user?.client_id && { client_id: user.client_id })
        }
    });

    const updateFilterOptions = (id, options) => {
        setFilters((prev) => prev.map((filter) => (filter.id === id ? { ...filter, options } : filter)));
    };

    // location data
    useEffect(() => {
        if (locationList) {
            setFilters((prev) => {
                const newFilters = [...prev];
                const locationFilter = newFilters.find((item) => item.id === 'locations');
                locationFilter.options = locationList.fields.map((item) => ({ id: item.id, name: item.name, isActive: item?.is_active }));
                return newFilters;
            });
        }
    }, [locationList]);

    // // therapist data
    useEffect(() => {
        if (therapistData?.fields) {
            const options = therapistData.fields.map(({ emp_id, name }) => ({
                id: emp_id,
                name
            }));
            updateFilterOptions('therapists', options);
        }
    }, [therapist]);

    useEffect(() => {
        const temp = [...filters];
        temp.forEach((fil) => {
            if (fil.id === 'service_id') {
                fil.options = serviceList?.fields
                    ?.filter((i) => ['ST', 'PT', 'OT', 'DT'].includes(i.service_code))
                    .map((i) => ({
                        id: i.id,
                        name: i.service_name
                    }));
            }
        });

        setFilters(temp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceList]);

    const handleFilterChange = (values) => {
        setFilterValues(values);
    };

    return (
        <MainCard
            title="Time Sheet"
            secondary={
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}
                >
                    <Autocomplete
                        sx={{ width: 250 }}
                        required
                        size="small"
                        open={therapistOpen}
                        value={therapist}
                        loading={therapistIsLoading}
                        options={therapistData?.fields || []}
                        getOptionLabel={(option) => getEmployeeName(option)}
                        onChange={(e, v) => {
                            setTherapist(v);
                            setTherapistOpen(false);
                        }}
                        onOpen={() => setTherapistOpen(true)}
                        onClose={() => setTherapistOpen(false)}
                        renderOption={(props, option) => (
                            <li {...props} key={option?.emp_id}>
                                {getEmployeeName(option)}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Therapist"
                                variant="outlined"
                                fullWidth
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'off'
                                }}
                                onMouseDown={() => setTherapistOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown' || e.key === 'Enter') {
                                        setTherapistOpen(true);
                                    }
                                }}
                            />
                        )}
                    />
                    <Filters.Root
                        isLoading={locationIsLoading || serviceIsLoading}
                        dateRageKeys={['startDate', 'endDate']}
                        value={filterValues}
                        filters={filters}
                        therapist={therapist}
                        onChange={handleFilterChange}
                    >
                        <Filters.Datepicker sx={{ my: 2 }} label="From Date" id="startDate" />
                        <Filters.Datepicker label="To Date" id="endDate" />
                    </Filters.Root>
                    {hasPermission(['time_entry_add']) && (
                        <Button variant="contained" onClick={() => navigate('/add/activity-time-sheet')}>
                            Add Time Sheet
                        </Button>
                    )}
                </Stack>
            }
        >
            <Box>
                <CTabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="In Progress" {...a11yProps(0)} />
                    <Tab label="Signed" {...a11yProps(1)} />
                </CTabs>

                <TabPanel value={tabIndex} index={0}>
                    <InProgress filterValues={filterValues} therapist={therapist} />
                </TabPanel>

                <TabPanel value={tabIndex} index={1}>
                    <Signed filterValues={filterValues} therapist={therapist} />
                </TabPanel>
            </Box>
        </MainCard>
    );
};

export default ListActivityTimeSheet;
